import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { CreateProjectModal } from '../components/projects/CreateProjectModal.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import {
  useProjects,
  useBulkArchiveProjects,
  useBulkDeleteProjects,
  type ProjectRow,
} from '../hooks/useProjects.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { useToast } from '../components/ui/Toast.js';

// Active statuses shown in dropdown 1 (archived excluded — it's in dropdown 2)
// 'content_planning' faltaba acá (bug real, 2026-08-02): es un status
// server-alcanzable (routes/projects.ts transiciona briefed -> content_planning
// automáticamente) pero no aparecía en este filtro — un proyecto ahí quedaba
// imposible de encontrar por status en esta lista.
const STATUS_ACTIVE = ['draft', 'briefed', 'content_planning', 'in_design', 'client_review', 'revisions', 'approved', 'delivered'];

const STATUS_COLOR: Record<string, string> = {
  draft:             'var(--evo-fg-3)',
  briefed:           'var(--evo-blue, #3b82f6)',
  content_planning:  'var(--evo-cyan)',
  in_design:         'var(--evo-cyan)',
  client_review:     'var(--evo-amber)',
  revisions:         'var(--evo-amber)',
  approved:          'var(--evo-green, #22c55e)',
  delivered:         'var(--evo-info, #0ea5e9)',
  archived:          'var(--evo-fg-4)',
};

// Checkbox col width — identical to LeadsPage so visual rhythm matches
const GRID_COLS = 'calc(var(--evo-list-pad-x) + 16px + var(--evo-list-checkbox-gap)) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr)';

const COL_HEADER = {
  padding: '8px 12px',
  color: 'var(--evo-fg-3)',
  fontSize: 'var(--evo-list-header-fs)',
  fontWeight: 500 as const,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.88px',
  fontFamily: 'var(--evo-font)',
};

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleDateString('es', { month: 'short', day: 'numeric', year: '2-digit' });
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const color = STATUS_COLOR[status] ?? 'var(--evo-fg-3)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      height: '18px', padding: '0 8px',
      fontSize: '10px', fontWeight: 500, fontFamily: 'var(--evo-font)',
      color, border: `1px solid ${color}`, borderRadius: 100,
      background: 'transparent', whiteSpace: 'nowrap', lineHeight: 1,
    }}>
      {t(`projects.status.${status}`, { defaultValue: status })}
    </span>
  );
}

export function ProjectsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setOverride } = useTopBarCtx();
  const toast = useToast();

  const [statusFilter, setStatusFilter] = useState('');
  // viewFilter values: '' | 'type:website' | 'subtype:single_page' | 'archived'
  const [viewFilter, setViewFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());

  const isArchivedView = viewFilter === 'archived';

  const queryFilters = {
    ...(isArchivedView ? { viewArchived: true as const } : {}),
    ...(!isArchivedView && statusFilter ? { status: statusFilter } : {}),
    ...(!isArchivedView && viewFilter.startsWith('type:') ? { type: viewFilter.slice(5) } : {}),
    ...(!isArchivedView && viewFilter.startsWith('subtype:') ? { subtype: viewFilter.slice(8) } : {}),
    limit: 200,
  };

  const { data, isLoading } = useProjects(queryFilters);
  const bulkArchive = useBulkArchiveProjects();
  const bulkDelete = useBulkDeleteProjects();

  const items: ProjectRow[] = data?.items ?? [];

  const filtered = search.trim()
    ? items.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.slug.toLowerCase().includes(search.toLowerCase()))
    : items;

  useEffect(() => {
    setOverride({
      title: t('projects.title'),
      heroIcon: 'projects-large',
      heroIconColor: 'var(--evo-steel)',
      bottomActions: (
        <PillGroup size="md">
          <Pill tone="cyan" icon="plus" onClick={() => setShowCreate(true)}>
            {t('projects.newProject')}
          </Pill>
        </PillGroup>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t]);

  function toggleSelect(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleBulkArchive() {
    const ids = Array.from(selected);
    bulkArchive.mutate(ids, {
      onSuccess: (res) => {
        setSelected(new Set());
        toast.success(t('projects.bulk.archived', { count: res.updated }));
      },
      onError: () => toast.error(t('leads.bulkActions.failed')),
    });
  }

  function handleBulkDelete() {
    const ids = Array.from(selected);
    bulkDelete.mutate(ids, {
      onSuccess: (res) => {
        setSelected(new Set());
        toast.success(t('projects.bulk.deleted', { count: res.deleted }));
      },
      onError: () => toast.error(t('leads.bulkActions.failed')),
    });
  }

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">

      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder={t('projects.searchPlaceholder')}
        />
        {/* Dropdown 1: active statuses only (ignored when in archived view) */}
        <FilterRow.Dropdown<string>
          value={isArchivedView ? '' : statusFilter}
          placeholder={t('projects.allStatuses')}
          options={[
            { value: '', label: t('projects.allStatuses') },
            ...STATUS_ACTIVE.map(s => ({ value: s, label: t(`projects.status.${s}`, { defaultValue: s }) })),
          ]}
          onChange={val => { if (!isArchivedView) setStatusFilter(val ?? ''); }}
        />
        {/* Dropdown 2: view / type / archived */}
        <FilterRow.Dropdown<string>
          value={viewFilter}
          placeholder={t('projects.filters.allProjects')}
          options={[
            { value: '', label: t('projects.filters.allProjects') },
            { value: 'type:website', label: t('projects.filters.typeWebsite') },
            { value: 'subtype:single_page', label: t('projects.filters.subtypeSinglePage') },
            { value: 'archived', label: t('projects.filters.archived') },
          ]}
          onChange={val => {
            const v = val ?? '';
            setViewFilter(v);
            if (v === 'archived') setStatusFilter('');
          }}
        />
      </FilterRow>

      <CreateProjectModal open={showCreate} onClose={() => setShowCreate(false)} />

      <SelectionActionBar
        visible={selected.size > 0}
        countLabel={t('projects.selected', { count: selected.size })}
        onClearSelection={() => setSelected(new Set())}
      >
        <SelectionActionBar.Action
          label={t('projects.bulk.archive')}
          tone="amber"
          loading={bulkArchive.isPending}
          onClick={handleBulkArchive}
          confirmRequired
        />
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action
          label={t('common.delete')}
          tone="danger"
          loading={bulkDelete.isPending}
          onClick={handleBulkDelete}
          confirmRequired
        />
      </SelectionActionBar>

      <div className="flex-1 overflow-hidden flex flex-col">

        {/* Column headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: GRID_COLS,
          height: 'var(--evo-toolbar-height)',
          alignItems: 'center',
          borderBottom: '1px solid var(--evo-stroke)',
          flexShrink: 0,
        }}>
          <div style={{ padding: '8px var(--evo-list-checkbox-gap) 8px var(--evo-list-pad-x)' }}>
            <Checkbox
              checked={filtered.length > 0 && filtered.every(p => selected.has(p.id))}
              indeterminate={selected.size > 0 && !filtered.every(p => selected.has(p.id))}
              onChange={_ => {
                if (filtered.every(p => selected.has(p.id))) {
                  filtered.forEach(p => { if (selected.has(p.id)) toggleSelect(p.id); });
                } else {
                  filtered.filter(p => !selected.has(p.id)).forEach(p => toggleSelect(p.id));
                }
              }}
            />
          </div>
          <div style={{ ...COL_HEADER, paddingLeft: 0 }}>{t('projects.columns.name')}</div>
          <div style={COL_HEADER}>{t('projects.columns.client')}</div>
          <div style={COL_HEADER}>{t('projects.columns.type')}</div>
          <div style={COL_HEADER}>{t('projects.columns.status')}</div>
          <div style={{ ...COL_HEADER, textAlign: 'right' as const }}>{t('projects.columns.created')}</div>
        </div>

        {/* List rows */}
        <div className="flex-1 overflow-auto">
          {isLoading && <LoadingState size="sm" />}
          {!isLoading && filtered.length === 0 && (
            <EmptyState size="md" reserveSpace title={t('projects.empty')} />
          )}
          {!isLoading && filtered.length > 0 && (
            <TimeBandedList<ProjectRow, number>
              items={filtered}
              getTimestamp={(p) => new Date(p.createdAt).toISOString()}
              getId={(p) => p.id}
              checkedIds={selected}
              onBandCheck={(ids, allChecked) => {
                ids.forEach(id => {
                  const isSelected = selected.has(id);
                  if (allChecked && isSelected) toggleSelect(id);
                  else if (!allChecked && !isSelected) toggleSelect(id);
                });
              }}
              collapsedBands={collapsedBands}
              onToggleBand={(band) => setCollapsedBands(prev => {
                const next = new Set(prev);
                next.has(band) ? next.delete(band) : next.add(band);
                return next;
              })}
              renderItem={(project) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div
                    data-list-item-id={project.id}
                    onClick={() => navigate(`/proyectos/${project.slug}`, {
                      state: {
                        filteredIds: filtered.map(p => p.slug),
                        filters: { search, statusFilter, viewFilter },
                      },
                    })}
                    className="cursor-pointer"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: GRID_COLS,
                      alignItems: 'flex-start',
                      minHeight: 'var(--evo-list-row-min-height)',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,250,200,0.04)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
                  >
                    {/* Checkbox */}
                    <div
                      style={{ padding: '14px var(--evo-list-checkbox-gap) 14px var(--evo-list-pad-x)' }}
                      onClick={e => { e.stopPropagation(); toggleSelect(project.id); }}
                    >
                      <div style={{ pointerEvents: 'none' }}>
                        <Checkbox checked={selected.has(project.id)} onChange={() => {}} />
                      </div>
                    </div>

                    {/* Name (primary) + Company (secondary) */}
                    <div style={{ padding: '14px 12px 14px 0', overflow: 'hidden' }}>
                      <div className="truncate" style={{
                        color: 'var(--evo-fg-1)', fontSize: 'var(--evo-list-title-fs)',
                        fontWeight: 400, fontFamily: 'var(--evo-font)',
                      }}>
                        {project.name}
                      </div>
                      <div className="truncate" style={{
                        color: 'var(--evo-fg-3)', marginTop: '2px',
                        fontSize: 'var(--evo-list-subtitle-fs)', fontFamily: 'var(--evo-font)',
                      }}>
                        {project.contactBusinessName ?? '—'}
                      </div>
                    </div>

                    {/* Client: firstName + lastName */}
                    <div style={{
                      padding: '14px 12px', fontSize: 'var(--evo-list-subtitle-fs)',
                      color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {[project.contactFirstName, project.contactLastName].filter(Boolean).join(' ') || '—'}
                    </div>

                    {/* Type · Subtype */}
                    <div style={{
                      padding: '14px 12px', fontSize: 'var(--evo-list-subtitle-fs)',
                      color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {t(`projects.type.${project.type}`, { defaultValue: project.type })}
                      {project.subtype && ` · ${t(`projects.subtype.${project.subtype}`, { defaultValue: project.subtype })}`}
                    </div>

                    {/* Status badge */}
                    <div style={{ padding: '14px 12px', display: 'flex' }}>
                      <StatusBadge status={project.status} />
                    </div>

                    {/* Created date */}
                    <div style={{
                      padding: '14px 12px', fontSize: 'var(--evo-list-subtitle-fs)',
                      color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)',
                      textAlign: 'right',
                    }}>
                      {fmtDate(project.createdAt)}
                    </div>
                  </div>
                  <div style={{ margin: '0 var(--evo-list-pad-x)', height: '1px', background: 'var(--evo-stroke)' }} />
                </div>
              )}
              emptyState={<EmptyState size="md" reserveSpace title={t('projects.empty')} />}
            />
          )}
        </div>
      </div>
    </div>
  );
}
